import React from 'react';
import type { MarketContext, RiskAssessment } from '../../types/yardi';

interface MarketIntelligenceCardProps {
  yardiRentGrowth: number;
  marketContext: MarketContext;
  riskAssessment?: RiskAssessment;
  source: string;
  isLoading?: boolean;
}

export const MarketIntelligenceCard: React.FC<MarketIntelligenceCardProps> = ({
  yardiRentGrowth,
  marketContext,
  riskAssessment,
  source,
  isLoading = false,
}) => {
  // Format number with fallback for null/undefined
  const formatValue = (value: number | undefined, suffix: string = '%'): string => {
    if (value === undefined || value === null) return '--';
    return `${(value * 100).toFixed(1)}${suffix}`;
  };

  // Get risk level and color
  const getRiskLevel = (): { label: string; color: string } => {
    if (!riskAssessment) return { label: 'N/A', color: 'text-slate-400' };

    const score = riskAssessment.composite_score;
    if (score <= 10) return { label: 'LOW', color: 'text-emerald-400' };
    if (score <= 30) return { label: 'MEDIUM', color: 'text-amber-400' };
    return { label: 'HIGH', color: 'text-red-400' };
  };

  const riskLevel = getRiskLevel();

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="h-6 w-48 bg-slate-700 rounded animate-pulse" />
          <div className="h-5 w-32 bg-slate-700 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-slate-900/50 rounded-lg p-3">
              <div className="h-4 w-24 bg-slate-700 rounded animate-pulse mb-2" />
              <div className="h-6 w-16 bg-slate-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="mt-4 h-3 w-40 bg-slate-700 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <svg
            className="w-6 h-6 text-amber-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-slate-100">Market Intelligence</h3>
        </div>
        <div className="text-sm font-medium text-amber-400 border border-amber-400/30 rounded px-2 py-1">
          Yardi Matrix
        </div>
      </div>

      {/* 6-Metric Grid (3x2) */}
      <div className="grid grid-cols-3 gap-4">
        {/* Row 1 */}
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-sm text-slate-400 mb-1">Rent Growth</div>
          <div className={`text-xl font-semibold ${yardiRentGrowth > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {formatValue(yardiRentGrowth)}
          </div>
        </div>

        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-sm text-slate-400 mb-1">2026 Forecast</div>
          <div className="text-xl font-semibold text-amber-400">
            {formatValue(marketContext.forecast_2026)}
          </div>
        </div>

        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-sm text-slate-400 mb-1">Job Growth</div>
          <div className="text-xl font-semibold text-amber-400">
            {formatValue(marketContext.job_growth)}
          </div>
        </div>

        {/* Row 2 */}
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-sm text-slate-400 mb-1">Completions</div>
          <div className="text-xl font-semibold text-slate-300">
            {formatValue(marketContext.completions_pct)}
          </div>
        </div>

        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-sm text-slate-400 mb-1">Absorption</div>
          <div className="text-xl font-semibold text-slate-300">
            {formatValue(marketContext.absorption)}
          </div>
        </div>

        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-sm text-slate-400 mb-1">Risk Score</div>
          <div className="flex items-center justify-center gap-1">
            <span className="text-xl font-semibold text-slate-300">
              {riskAssessment?.composite_score.toFixed(1) ?? '--'}
            </span>
            <span className={`text-sm font-bold ${riskLevel.color}`}>
              {riskLevel.label}
            </span>
          </div>
        </div>
      </div>

      {/* Source footer */}
      <div className="mt-4 text-sm text-slate-500">
        Source: {source || 'Yardi Matrix National Multifamily Report'}
      </div>
    </div>
  );
};
